import { graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef } from 'react';
import { ChevronRight, InfoCircle } from 'react-bootstrap-icons';

import ArticlesHorizontalList from '../components/article/articles-horizontal-list';
import BioAgeSection from '../components/bioage/bioage-section';
import CampaignProduct from '../components/campaign/campaign-product';
import FaqMini from '../components/faq/mini';
import FaqProduct from '../components/faq/product';
import Framework from '../components/framework';
import AlwaysIncluded from '../components/framework/always-included';
import Breadcrumb from '../components/framework/breadcrumb';
import ExpandableText from '../components/framework/expandeble/expandable-text';
import Included from '../components/framework/included';
import Link from '../components/framework/link';
import SectionHeader from '../components/framework/sections/section-header';
import SectionSatisfiedCustomerGuarantee from '../components/framework/sections/section-satisfied-customer-guarantee';
import Slider from '../components/framework/slider';
import HowItWorks from '../components/how-it-works/how-it-works';
import HowItWorksFeature from '../components/how-it-works/how-it-works-feature';
import Why from '../components/how-it-works/why';
import LocationList from '../components/location/list';
import HumanModelProductViewer from '../components/rontgen/human_body_product_viewer';
import Klarna from '../components/script/klarna';
import Trustpilot from '../components/script/trustpilot';
import Addons from '../components/shop/addons';
import AffiliateAbout from '../components/shop/affiliate_about';
import PriceItem from '../components/shop/price_item';
import MarkerThumb from '../components/test/marker-thumb';
import ProductBadges from '../components/test/product-badges';
import ProductImages from '../components/test/product-images';
import ProductList from '../components/test/product-list';
import ProductShopButton from '../components/test/product-shop-button';
import ServiceGroupTabs from '../components/test/service-group-tabs';
import Trans from '../components/translation/trans';
import BarTrust from '../components/trust/bar-trust';
import { StoreContext } from '../context/store';
import useCampaign from '../hooks/use_campaign';
import useScrollPosition from '../hooks/use_scroll_position';
import useSiteMetadata from '../hooks/use_site_metadata';
import useTranslation from '../hooks/use_translation';
import { getProductGroup, getVariantByLanguage } from '../utils/helpers';
import { createEvent } from '../utils/tracking';

const ProductItem = ({ data, location }) => {
	let group = null;
	let group_id = 0;
	const cartScrollRef = useRef();
	const hasSentViewItem = useRef(false);
	const siteMetadata = useSiteMetadata();
	const { language, t } = useTranslation('template_product');
	const { isActive } = useCampaign();

	const product = data.newProduct;
	const group_data = getProductGroup(data.allGroup.nodes, product);
	if (group_data) {
		group_id = group_data.group_id;
		group = getVariantByLanguage(language, group_data.group_variants);
	}

	const image_header = product?.fields?.files_header_url_src;
	const variant = getVariantByLanguage(language, product.product_variants);

	const faq_items = data.allArticle.nodes
		?.filter((item) => item?.products?.includes(product.product_id))
		?.map((item) => {
			const article_variant = getVariantByLanguage(
				language,
				item.article_variants,
			);
			if (!article_variant) return;

			return {
				answer: article_variant.content,
				question: article_variant.title,
			};
		})
		.filter(Boolean);

	const handleScrollClick = (event, id) => {
		let header_height = 0;

		event.preventDefault();

		const header_element = document.getElementById('header_navigation');
		if (header_element?.offsetHeight) {
			header_height = header_element?.offsetHeight;
		}

		const element = document.getElementById(id);
		if (element?.offsetTop) {
			window.scrollTo({
				top: element.offsetTop - header_height,
			});
		}
	};

	const { isItemInCart } = useContext(StoreContext);
	const inCart = isItemInCart({
		markers: product.markers,
		product_id: product.product_id,
		product_settings: product.product_settings,
		product_type: product.type,
	});

	const scrollPosition = useScrollPosition();
	const showCartBar =
		scrollPosition > 800 && scrollPosition < cartScrollRef?.current?.offsetTop;

	useEffect(() => {
		if (hasSentViewItem.current && variant.title) {
			createEvent('view_item', {
				currency: variant.currency.code,
				items: [
					{
						amount: variant.price,
						product_id: product.product_id,
						quantity: 1,
						title: variant.title,
					},
				],
				value: variant.price,
			});

			hasSentViewItem.current = true;
		}
	}, [product, variant]);

	return (
		<Framework
			description={variant.seo_description}
			location={location}
			schema={{
				faq: {
					items: faq_items,
				},
				product: {
					currency: variant.currency.code,
					description: variant?.excerpt,
					id: product.product_id,
					image: getSrc(image_header),
					price: variant.price,
					title: variant.title,
					variant: {
						id: variant.product_variant_id,
					},
				},
			}}
			tags={variant.seo_keywords}
			title={variant.seo_title}
			tracking={{ content_group: 'Product' }}
			type="product"
		>
			{inCart.status == 'no' && variant.state !== 'affiliate' && (
				<div
					className={`bar-title fixed-bottom bg-white border-top py-2 ${
						showCartBar ? 'bar-title-active slide-up' : 'slide-down'
					} `}
				>
					<div className="container">
						<div className="row align-items-center">
							<div className="col d-lg-block d-none">
								<div className="row align-items-center">
									<div className="col-auto pe-0 pe-lg-2">
										<ProductImages
											group={group}
											group_id={group_id}
											onlyFirstImage
											product={product}
											size="sm"
											variant={variant}
										/>
									</div>
									<div className="col">
										<p className="small text-dark mb-0 pe-5">{variant.title}</p>

										<span className={`small fw-700 text-number mb-0`}>
											<PriceItem
												currency={variant.currency.code}
												price={variant.price}
												price_discount={variant.price_discount}
											/>
										</span>
									</div>
								</div>
							</div>
							<div className="col col-lg-auto">
								<ProductShopButton
									product_id={product.product_id}
									tracking={{
										item_list_name: 'Product page',
										source: 'item',
									}}
									variant="product_item"
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			<section className="mt-lg-3 mt-2 product" id="content">
				<div
					aria-hidden="true"
					aria-labelledby="add-more-modal-label"
					className="modal fade"
					id="add-more-modal"
					tabIndex="-1"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h3 className="modal-title fs-5" id="add-more-modal-label">
									{t('t23')}
								</h3>
								<button
									aria-label="Close"
									className="btn-close"
									data-bs-dismiss="modal"
									type="button"
								></button>
							</div>
							<div className="modal-body">
								{t('t24')}
								<div className="alert bg-light mt-3 xs d-flex gap-2 align-items-center">
									{t('t25')}
								</div>
							</div>
							<div className="modal-footer">
								<button
									className="btn btn-accent"
									data-bs-dismiss="modal"
									type="button"
								>
									{t('t26')}
								</button>
							</div>
						</div>
					</div>
				</div>
				<div className="container px-0">
					<nav aria-label="breadcrumb mb-2">
						<Breadcrumb
							className="horizontal-list mb-1 mb-lg-3"
							links={[
								{
									base_url: '/tester',
									id:
										product.groups && product.groups.length
											? product.groups[0]
											: null,
									type: 'group',
								},
								{ active: true, title: variant.title },
							]}
							test={true}
						/>
					</nav>
				</div>

				{product.status == 'archived' && (
					<div className="container">
						<div className="alert alert-warning my-3" role="alert">
							{t('t11')}
						</div>
					</div>
				)}

				<div className="container grid-product-item  position-relative ">
					<div className="position-relative grid-product-item-header mb-4 mb-lg-0">
						{product?.locations?.length > 0 && (
							<div
								className="alert alert-warning alert-dismissible fade show"
								role="alert"
							>
								<InfoCircle className="me-1" />
								<Trans i18nKey="t22" t={t} />
								<button
									aria-label="Close"
									className="btn-close"
									data-bs-dismiss="alert"
									type="button"
								></button>
							</div>
						)}
						<div className="d-flex align-items-center mb-4">
							{!image_header && (
								<div className="me-2 pe-0 pe-lg-2">
									<MarkerThumb
										groups={[group_id]}
										markers={product.markers}
										product_type={product.type}
										size="lg"
									/>
								</div>
							)}

							<div>
								{variant.subtitle && (
									<h2 className="x-small title-small mb-0">
										{variant.subtitle}
									</h2>
								)}
								<h1 className="section-title text-break text-dark mb-1 pe-5">
									{variant.title}
								</h1>
								<div className="">
									<ProductBadges product_id={product.product_id} />
								</div>
								{product?.markers?.length > 0 && (
									<div className="mt-3">
										<Included product_settings={product.product_settings} />
									</div>
								)}
							</div>
						</div>

						<div className={`title-medium fw-500 mt-1 text-number mb-0`}>
							<PriceItem
								currency={variant.currency.code}
								price={variant.price}
								price_discount={variant.price_discount}
							/>
						</div>

						{variant.state === 'coming' && (
							<div className="text-white bg-dark badge x-small mb-1">
								{t('t8')}
							</div>
						)}
					</div>
					<ProductImages
						group={group}
						group_id={group_id}
						onlyFirstImage={variant.state == 'affiliate'}
						product={product}
						variant={variant}
					/>

					<div className="grid-product-item-side">
						<div className="mt-3 pe-4 product-excerpt">
							<Trans defaults={variant.excerpt} t={t} />
						</div>

						<Link
							className="small mb-4"
							onClick={(event) => handleScrollClick(event, 'information')}
							to="#information"
						>
							{t('t9')}
						</Link>
						<div className="mt-4">
							<ProductShopButton
								className="mt-3"
								product_id={product.product_id}
								tracking={{ item_list_name: 'Product page', source: 'item' }}
								variant="product_item"
							/>
						</div>

						{variant.state != 'affiliate' && (
							<div className="mt-4">
								<Klarna
									data-key="credit-promotion-auto-size"
									data-purchase-amount={Math.round(
										variant.price_discount > 0
											? variant.price_discount * 100
											: variant.price * 100,
									)}
									data-theme="default"
									id="klarna-product"
								/>
							</div>
						)}

						{product?.products?.length > 0 && (
							<ProductList
								exclude_product_types={['addon']}
								grouping={false}
								include_products={product.products}
								tracking={{
									item_list_name: 'Related products on product item page',
									source: 'related',
								}}
								variant="slim"
							>
								{(component) => (
									<>
										{component && (
											<div className="">
												<h3 className="accordion-button-fake fw-800 py-0 mt-4 text-decoration-none">
													{t('t7')}
												</h3>
												<hr className="mb-0" />

												{component}
											</div>
										)}
									</>
								)}
							</ProductList>
						)}

						{variant.state != 'affiliate' && (
							<>
								{product?.markers?.length > 0 && (
									<Addons
										exclude_products={[product.product_id]}
										product_id={product.product_id}
										product_settings={product.product_settings}
									/>
								)}

								{variant.price_discount > 0 && isActive && (
									<div className="mt-4">
										<CampaignProduct />
									</div>
								)}

								<div className="mt-3 bg-gray-100 rounded-3 p-3">
									<BarTrust size="sm" type={product?.diagnostics?.[0]?.type} />
								</div>
								<div className="mt-4">
									<Trustpilot type="micro_trust_score" />
								</div>
							</>
						)}
					</div>
				</div>
			</section>
			<hr />
			<div className="container mt-5 grid-product-information" id="information">
				<div className="grid-product-information-main">
					<div
						className="accordion accordion-flush border-bottom border-1"
						id="product-information"
					>
						<div className="accordion-item">
							<h4
								className="accordion-header"
								id="accordion-header-product-information-1"
							>
								<button
									aria-controls="product-information-content"
									aria-expanded="false"
									className="accordion-button collapsed"
									data-bs-target="#product-information-content"
									data-bs-toggle="collapse"
									type="button"
								>
									<Trans
										i18nKey="t2"
										t={t}
										values={{
											product_title: variant.title,
										}}
									/>
								</button>
							</h4>
							<div
								aria-labelledby="accordion-header-product-information"
								className="accordion-collapse collapse show"
								id="product-information-content"
							>
								<div className="accordion-body text-format expandable-text-container pb-4">
									<ExpandableText maxHeight={200}>
										<Trans defaults={variant.content} t={t} />
									</ExpandableText>
								</div>
							</div>
						</div>
					</div>
				</div>

				{product.markers.length > 0 && (
					<div className="grid-product-information-markers">
						<div
							className="accordion accordion-flush border-bottom"
							id="markers"
						>
							<div className="accordion-item">
								<h4 className="accordion-header" id="markers">
									<button
										aria-controls="markers-content"
										aria-expanded="false"
										className="accordion-button collapsed"
										data-bs-target="#markers-content"
										data-bs-toggle="collapse"
										type="button"
									>
										{t('t3')}
										<div className="biomarkers ms-1">
											<span className="text-number">
												{product.markers.length}
											</span>
										</div>
									</button>
								</h4>
								<div
									aria-labelledby="markers"
									className="accordion-collapse collapse show"
									id="markers-content"
								>
									<div className="accordion-body accordion-body-sm bg-gray-100 rounded-3 px-3 py-0">
										<ServiceGroupTabs include_markers={product.markers} />
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="grid-product-information-rest">
					<div className="accordion accordion-flush">
						{/* INFÖR PROVTAGNINGEN */}
						{variant.state != 'affiliate' &&
							(product?.diagnostics?.[0]?.type == 'blood_sample' ||
								product?.diagnostics?.[0]?.type == 'mri') && (
								<div className="accordion-item ">
									<h4 className="accordion-header" id="specification">
										<button
											aria-controls="specification-content"
											aria-expanded="false"
											className="accordion-button collapsed"
											data-bs-target="#specification-content"
											data-bs-toggle="collapse"
											type="button"
										>
											{product?.diagnostics?.[0]?.type == 'blood_sample'
												? t('t4')
												: t('t28')}
										</button>
									</h4>
									<div
										aria-labelledby="specification"
										className="accordion-collapse collapse"
										id="specification-content"
									>
										<div className="accordion-body text-format">
											<Trans
												i18nKey={
													product?.diagnostics?.[0]?.type == 'blood_sample'
														? 't17'
														: 't29'
												}
												t={t}
											/>
											{product?.diagnostics?.[0]?.type == 'blood_sample' && (
												<>
													<ul>
														{product.product_settings?.requirement_fasting && (
															<Trans i18nKey="t5" t={t} />
														)}
														{product.product_settings?.requirement_morning && (
															<Trans i18nKey="t19" t={t} />
														)}
														{product.product_settings
															?.requirement_daily_variation && (
															<Trans i18nKey="t20" t={t} />
														)}
														{product.product_settings?.requirement_medicine && (
															<Trans i18nKey="t21" t={t} />
														)}
														{product.product_settings
															?.requirement_awake_time && (
															<Trans
																i18nKey="t27"
																t={t}
																values={{
																	awake_time:
																		product.product_settings
																			?.requirement_awake_time,
																}}
															/>
														)}

														<Trans i18nKey="t18" t={t} />
													</ul>
													<Trans i18nKey="t16" t={t} />
												</>
											)}
										</div>
									</div>
								</div>
							)}
						{/* EFTER PROVTAGNINGEN */}
						{product?.diagnostics?.[0]?.type == 'mri' && (
							<div className="accordion-item ">
								<h4 className="accordion-header" id="specification-after">
									<button
										aria-controls="specification-content-after"
										aria-expanded="false"
										className="accordion-button collapsed"
										data-bs-target="#specification-content-after"
										data-bs-toggle="collapse"
										type="button"
									>
										{t('t31')}
									</button>
								</h4>
								<div
									aria-labelledby="specification-after"
									className="accordion-collapse collapse"
									id="specification-content-after"
								>
									<div className="accordion-body text-format">
										<Trans i18nKey={'t32'} t={t} />
									</div>
								</div>
							</div>
						)}
						<FaqProduct include_products={[product.product_id]}>
							{(component) => (
								<>
									{component && (
										<div className="accordion-item ">
											<h3 className="accordion-header" id="faq">
												<button
													aria-controls="faq-content"
													aria-expanded="false"
													className="accordion-button collapsed"
													data-bs-target="#faq-content"
													data-bs-toggle="collapse"
													type="button"
												>
													<Trans
														i18nKey="t13"
														t={t}
														values={{
															product_title: variant.title,
														}}
													/>
												</button>
											</h3>
											<div
												aria-labelledby="faq"
												className="accordion-collapse collapse"
												id="faq-content"
											>
												<div className="accordion-body accordion-body-sm bg-gray-100 rounded-3 py-0 px-3">
													<div
														className="accordion accordion-flush"
														id="product-faq"
													>
														{component}
													</div>
												</div>
											</div>
										</div>
									)}
								</>
							)}
						</FaqProduct>
					</div>

					{product?.locations?.length > 0 &&
						product?.diagnostics?.[0]?.type != 'ecg' && (
							<div className="accordion-item">
								<h4 className="accordion-header" id="locations">
									<button
										aria-controls="locations-content"
										aria-expanded="false"
										className="accordion-button collapsed"
										data-bs-target="#locations-content"
										data-bs-toggle="collapse"
										type="button"
									>
										{t('t14')}
									</button>
								</h4>
								<div
									aria-labelledby="locations"
									className="accordion-collapse collapse show"
									id="locations-content"
								>
									<div className="accordion-body accordion-body-sm">
										<LocationList include_locations={product.locations} />
									</div>
								</div>
							</div>
						)}

					{!product?.locations?.length &&
						product?.diagnostics?.[0]?.type != 'ecg' && (
							<h4 className="accordion-header border-top" id="locations">
								<Link
									className="accordion-button-fake fw-800 d-flex text-dark text-decoration-none align-items-center justify-content-between"
									to="/mottagningar"
								>
									<div className=" me-auto text-decoration-none">
										{t('t14')}
									</div>
									<div className="">
										<ChevronRight className="" />
									</div>
								</Link>
							</h4>
						)}
				</div>
			</div>

			{product.product_settings?.include_biological_age && (
				<section className="section" id="bio_age">
					<BioAgeSection />
				</section>
			)}

			{product?.markers?.length > 0 && (
				<ProductList
					exclude_products={[product.product_id]}
					grouping={false}
					include_groups={[group_id]}
					limit={10}
					tracking={{
						item_list_name:
							'Products in the same category on product item page',
						source: 'related',
					}}
					variant="card_small"
				>
					{(component) => (
						<>
							{component && (
								<section className="section" id="product-category-products">
									<div className="container">
										<div className="d-flex justify-content-between align-items-center mb-2">
											<SectionHeader
												groups={[group_id]}
												headingType="h3"
												linkType="group_link"
												page="tester"
												title={t('t12')}
											/>
										</div>
									</div>
									<div className="container px-lg-1 px-0">
										<Slider>{component}</Slider>
									</div>
								</section>
							)}
						</>
					)}
				</ProductList>
			)}
			{variant.state != 'affiliate' && (
				<>
					<section className="section" id="health-tests-how-it-works">
						<HowItWorks type={product?.diagnostics?.[0]?.type} />
					</section>
					<section className="container px-0 px-lg-3 mb-5">
						<HowItWorksFeature enableReadMore enableTitle type="service" />
					</section>
					{product?.diagnostics?.[0]?.type == 'mri' && (
						<section className=" bg-gray-100 py-5 " id="rontgen-product-list">
							<div className="container">
								<div className="row">
									<div className="col-xl-6">
										<HumanModelProductViewer />
									</div>
									<div className="col-xl-6 mt-3 mt-lg-0">
										<div
											className={`position-relative bg-white rounded-4 p-lg-5 p-3`}
										>
											<ExpandableText maxHeight={705} type={'show_more'}>
												<h3 className="title-small"> {t('t30')}</h3>
												<ProductList
													exclude_groups={
														siteMetadata.config.business_tests_groups
													}
													include_diagnostic_types={['mri']}
													tracking={{
														item_list_name: 'MR products on MR rontgen page',
														source: 'related',
													}}
													variant="slim"
												/>
											</ExpandableText>
										</div>
									</div>
								</div>
							</div>
						</section>
					)}
					{product?.diagnostics?.[0]?.type == 'blood_sample' && (
						<>
							<section className="section">
								<AlwaysIncluded />
							</section>

							<section className="section">
								<Why />
							</section>
						</>
					)}

					<FaqMini
						include_groups={
							siteMetadata?.config?.support_product_groups?.[
								product?.diagnostics?.[0]?.type
							]
						}
					>
						{(component) => (
							<>
								{component && (
									<section className="section" id="product-faq">
										<div className="container">
											<SectionHeader
												headingType="h3"
												title={t('t10')}
												to="/support/"
											/>
											<ExpandableText maxHeight={526} type={'show_more'}>
												{component}
											</ExpandableText>
										</div>
									</section>
								)}
							</>
						)}
					</FaqMini>
					<section className="section pt-0">
						<SectionSatisfiedCustomerGuarantee />
					</section>
					<ArticlesHorizontalList
						include_products={[product.product_id]}
						limit={10}
					>
						{(component) => (
							<>
								{component && (
									<section
										className="section"
										id="index-articles"
										ref={cartScrollRef}
									>
										<div className="container">
											<SectionHeader
												headingType="h4"
												title={t('t6')}
												to="/artiklar/"
											/>

											<Slider>{component}</Slider>
										</div>
									</section>
								)}
							</>
						)}
					</ArticlesHorizontalList>
				</>
			)}
			{variant.state == 'affiliate' &&
				product?.product_brand?.product_brand_id && (
					<>
						<FaqMini include_products={[product.product_id]}>
							{(component) => (
								<>
									{component && (
										<section className="section" id="product-faq">
											<div className="container">
												<SectionHeader
													headingType="h3"
													title={t('t10')}
													to="/support/"
												/>

												{component}
											</div>
										</section>
									)}
								</>
							)}
						</FaqMini>

						<section className="section border-top" id="">
							<div className="container affiliate-about">
								<AffiliateAbout
									product_brand_id={product.product_brand.product_brand_id}
								/>
							</div>
						</section>
					</>
				)}
		</Framework>
	);
};

ProductItem.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	markers: PropTypes.array,
};

export default ProductItem;

export const pageQuery = graphql`
	query ProductById($id: String!) {
		allArticle(
			filter: { type: { eq: "support" } }
			sort: { article_variants: { title: ASC } }
		) {
			nodes {
				article_variants {
					content
					language {
						code
					}
					title
				}
				products
			}
		}

		allGroup {
			nodes {
				group_id
				group_variants {
					language {
						code
					}
					slug
					title
				}
			}
		}

		newProduct(id: { eq: $id }) {
			diagnostics {
				type
			}
			default {
				group_id
			}
			id
			fields {
				files_header_url_src {
					childImageSharp {
						gatsbyImageData(layout: FULL_WIDTH)
					}
				}
				product_brand_files_logo_url_src {
					childImageSharp {
						gatsbyImageData(width: 64)
					}
				}
			}
			groups
			locations
			markers
			product_id
			product_brand {
				product_brand_id
				product_brand_variants {
					content
					language {
						code
					}
					title
				}
			}
			product_settings {
				include_biological_age
				include_infection_tracking
				include_statement
				requirement_awake_time
				requirement_daily_variation
				requirement_fasting
				requirement_medicine
				requirement_morning
			}
			product_variants {
				content
				currency {
					code
					symbol
				}
				excerpt
				language {
					code
				}
				price
				price_discount
				product_variant_id
				seo_description
				seo_keywords
				seo_title
				slug
				state
				title
				subtitle
			}
			products
			status
			type
		}

		locales: allLocale {
			...translationFields
		}
	}
`;
